import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  DocumentArrowDownIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";
import api from "../utils/api";
import ExportFormatsButton from "../components/ExportFormatsButton";

function InvestorsFilterPage() {
  const [filters, setFilters] = useState({
    // Personal Information
    buyer_name: "",
    mobile_number: "",
    person_place: "",

    // Wallet Information
    wallet_address: "",

    // Investment Information
    status: "",
    scheme_id: "",
    token_number: "",
    purchase_date: "",
    return_date: "",
    date_from: "",
    date_to: "",

    // Referral Information
    referred_by: "",
    referrer_wallet: "",

    // Transaction/Contract Stacking Information
    transaction_hash: "",
    transaction_date: "",
    from_wallet_address: "",
    to_wallet_address: "",
    usdt_received: "",
    usdt_received_min: "",
    usdt_received_max: "",
    transaction_remarks: "",
    unix_timestamp: "",
  });
  const [results, setResults] = useState([]);
  const [schemes, setSchemes] = useState([]);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // Fetch schemes on component mount
  useEffect(() => {
    const fetchSchemes = async () => {
      try {
        const response = await api.get("/api/schemes");
        setSchemes(response.data);
      } catch (error) {
        // Only log non-403 errors
        if (error.response?.status !== 403) {
          console.error("Error fetching schemes:", error);
        }
        // Silently fail for 403 - show empty schemes list
      }
    };
    fetchSchemes();
  }, []);

  const handleChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Debug: Check authentication
    const token = localStorage.getItem("token");
    console.log("Auth token present:", !!token);
    console.log("Token length:", token?.length || 0);

    try {
      const params = Object.entries(filters)
        .filter(([_, v]) => v)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join("&");

      console.log("Making filter request with params:", params);
      console.log("Full URL:", `/api/filter/investors?${params}`);

      const res = await api.get(`/api/filter/investors?${params}`);
      console.log("Filter response:", res.data);
      setResults(res.data);
      setError("");
    } catch (err) {
      console.error("Filter Error:", err);
      console.error("Error response:", err.response);
      const errorMessage =
        err.response?.data?.message ||
        err.response?.statusText ||
        err.message ||
        "Filter failed - Unknown error";
      setError(
        `Error: ${errorMessage} (Status: ${err.response?.status || "Unknown"})`
      );
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setFilters({
      // Personal Information
      buyer_name: "",
      mobile_number: "",
      person_place: "",

      // Wallet Information
      wallet_address: "",

      // Investment Information
      status: "",
      scheme_id: "",
      token_number: "",
      purchase_date: "",
      return_date: "",
      date_from: "",
      date_to: "",

      // Referral Information
      referred_by: "",
      referrer_wallet: "",

      // Transaction/Contract Stacking Information
      transaction_hash: "",
      transaction_date: "",
      from_wallet_address: "",
      to_wallet_address: "",
      usdt_received: "",
      usdt_received_min: "",
      usdt_received_max: "",
      transaction_remarks: "",
      unix_timestamp: "",
    });
    setResults([]);
    setError("");
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert("Copied to clipboard!");
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Enhanced Navigation Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Link
                to="/dashboard"
                className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                <HomeIcon className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Link>
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>•</span>
                <span>Advanced Search & Filtering</span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <button
                onClick={clearFilters}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
              >
                Clear Filters
              </button>
              <div className="bg-blue-50 px-3 py-1 rounded-full">
                <span className="text-sm font-medium text-blue-700">
                  Advanced Search
                </span>
              </div>
            </div>
          </div>

          {/* Page Title and Description */}
          <div className="mt-4">
            <div className="flex items-center gap-3 mb-2">
              <FunnelIcon className="h-8 w-8 text-gray-700" />
              <h1 className="text-3xl font-bold text-gray-900">
                Advanced Investor Search
              </h1>
            </div>
            <p className="text-gray-600 max-w-4xl">
              Comprehensive search across all investor data fields including
              personal information, investment details, wallet addresses,
              referral data, transaction records, and contract stacking
              information. Use any combination of filters to find exactly what
              you need. Export filtered results in multiple formats.
            </p>
            <div className="mt-3 flex flex-wrap gap-2 text-sm">
              <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                📊 {Object.keys(filters).length} Search Fields Available
              </span>
              <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full">
                🔍 Real-time Filtering
              </span>
              <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full">
                📤 Multi-format Export
              </span>
              <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                💰 USDT Range Search
              </span>
            </div>
          </div>
        </div>
        {/* Filter Form */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="flex items-center mb-4">
              <FunnelIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h3 className="text-lg font-medium text-gray-900">
                Comprehensive Search Criteria
              </h3>
              <div className="ml-4 text-sm text-gray-500">
                Search across all investor data fields
              </div>
            </div>

            <form onSubmit={handleSearch} className="space-y-6">
              {/* Personal Information Section */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                  👤 Personal Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Buyer Name
                    </label>
                    <input
                      name="buyer_name"
                      placeholder="Enter buyer name"
                      value={filters.buyer_name}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mobile Number
                    </label>
                    <input
                      name="mobile_number"
                      placeholder="Enter mobile number"
                      value={filters.mobile_number}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Person Place
                    </label>
                    <input
                      name="person_place"
                      placeholder="Enter location/place"
                      value={filters.person_place}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Investment Information Section */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                  💰 Investment Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      value={filters.status}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Statuses</option>
                      <option value="With UNS">With UNS</option>
                      <option value="Returned">Returned</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Investment Scheme
                    </label>
                    <select
                      name="scheme_id"
                      value={filters.scheme_id}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="">All Schemes</option>
                      {schemes
                        .filter(
                          (scheme) => scheme.parent_id || scheme.code !== "SLL"
                        )
                        .map((scheme) => (
                          <option key={scheme.id} value={scheme.id}>
                            {scheme.name}
                          </option>
                        ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Token Number
                    </label>
                    <input
                      name="token_number"
                      placeholder="Enter token number"
                      value={filters.token_number}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Purchase Date
                    </label>
                    <input
                      name="purchase_date"
                      type="date"
                      value={filters.purchase_date}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Return Date
                    </label>
                    <input
                      name="return_date"
                      type="date"
                      value={filters.return_date}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Date Range (Purchase)
                    </label>
                    <div className="flex space-x-2">
                      <input
                        name="date_from"
                        type="date"
                        placeholder="From Date"
                        value={filters.date_from}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        name="date_to"
                        type="date"
                        placeholder="To Date"
                        value={filters.date_to}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Wallet Information Section */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                  🔑 Wallet Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Wallet Address
                    </label>
                    <input
                      name="wallet_address"
                      placeholder="Enter wallet address"
                      value={filters.wallet_address}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      From Wallet Address
                    </label>
                    <input
                      name="from_wallet_address"
                      placeholder="Enter source wallet"
                      value={filters.from_wallet_address}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      To Wallet Address
                    </label>
                    <input
                      name="to_wallet_address"
                      placeholder="Enter destination wallet"
                      value={filters.to_wallet_address}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Referral Information Section */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                  👥 Referral Information
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Referred By
                    </label>
                    <input
                      name="referred_by"
                      placeholder="Enter referrer name"
                      value={filters.referred_by}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Referrer Wallet
                    </label>
                    <input
                      name="referrer_wallet"
                      placeholder="Enter referrer wallet"
                      value={filters.referrer_wallet}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              {/* Transaction/Contract Stacking Information Section */}
              <div>
                <h4 className="text-md font-semibold text-gray-800 mb-3 pb-2 border-b border-gray-200">
                  🔗 Transaction & Contract Stacking
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transaction Hash
                    </label>
                    <input
                      name="transaction_hash"
                      placeholder="Enter transaction hash"
                      value={filters.transaction_hash}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transaction Date
                    </label>
                    <input
                      name="transaction_date"
                      placeholder="Enter transaction date"
                      value={filters.transaction_date}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Unix Timestamp
                    </label>
                    <input
                      name="unix_timestamp"
                      type="number"
                      placeholder="Enter unix timestamp"
                      value={filters.unix_timestamp}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      USDT Received (Exact)
                    </label>
                    <input
                      name="usdt_received"
                      type="number"
                      step="0.01"
                      placeholder="Enter exact USDT amount"
                      value={filters.usdt_received}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      USDT Range (Min - Max)
                    </label>
                    <div className="flex space-x-2">
                      <input
                        name="usdt_received_min"
                        type="number"
                        step="0.01"
                        placeholder="Min"
                        value={filters.usdt_received_min}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                      <input
                        name="usdt_received_max"
                        type="number"
                        step="0.01"
                        placeholder="Max"
                        value={filters.usdt_received_max}
                        onChange={handleChange}
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Transaction Remarks
                    </label>
                    <input
                      name="transaction_remarks"
                      placeholder="Enter transaction remarks"
                      value={filters.transaction_remarks}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={clearFilters}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Clear All
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <MagnifyingGlassIcon className="h-4 w-4 mr-2" />
                  {isLoading ? "Searching..." : "Search All Fields"}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-red-400"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Results Summary */}
        {results.length > 0 && (
          <div className="bg-white border-2 border-blue-100 rounded-xl shadow-md hover:shadow-lg transition-all duration-300 p-6 mb-6">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center mr-3">
                  <DocumentArrowDownIcon className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold text-gray-900">
                    {results.length} Investor{results.length !== 1 ? "s" : ""}{" "}
                    Found
                  </p>
                  <p className="text-sm text-gray-600">
                    Matching your search criteria
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <div className="flex items-center space-x-2">
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    ✓ Search Complete
                  </span>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    📊 {results.length} Records
                  </span>
                </div>

                <div className="flex items-center space-x-3 pl-3 border-l border-gray-200">
                  <div className="flex items-center space-x-2">
                    <svg
                      className="w-4 h-4 text-gray-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                      />
                    </svg>
                    <span className="text-sm font-medium text-gray-700">
                      Export Results:
                    </span>
                  </div>
                  <div className="transform hover:scale-105 transition-transform duration-200">
                    <ExportFormatsButton filters={filters} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        {/* Results Table */}
        {results.length > 0 && (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Buyer Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact & Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Wallet Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Investment Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Dates
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Referral Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Transaction Data
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {results.map((inv) => (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          #{inv.id}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {inv.buyer_name}
                        </div>
                        {inv.token_number && (
                          <div className="text-xs text-gray-500">
                            Token: {inv.token_number}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          📱 {inv.mobile_number}
                        </div>
                        {inv.person_place && (
                          <div className="text-xs text-gray-500">
                            📍 {inv.person_place}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <span className="text-sm text-gray-900 font-mono break-all">
                            {inv.wallet_address
                              ? `${inv.wallet_address.substring(
                                  0,
                                  10
                                )}...${inv.wallet_address.substring(-8)}`
                              : "N/A"}
                          </span>
                          {inv.wallet_address && (
                            <button
                              onClick={() =>
                                copyToClipboard(inv.wallet_address)
                              }
                              className="text-blue-600 hover:text-blue-900 text-xs"
                            >
                              Copy
                            </button>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            inv.status === "With UNS"
                              ? "bg-green-100 text-green-800"
                              : "bg-purple-100 text-purple-800"
                          }`}
                        >
                          {inv.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {inv.scheme_name || "Unknown Scheme"}
                        </div>
                        {inv.usdt_received && (
                          <div className="text-xs text-green-600 font-medium">
                            💰 ${inv.usdt_received} USDT
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          📅{" "}
                          {inv.purchase_date
                            ? new Date(inv.purchase_date).toLocaleDateString()
                            : "N/A"}
                        </div>
                        <div className="text-xs text-gray-500">
                          {inv.status === "Returned"
                            ? inv.return_date &&
                              inv.return_date !== "9999-12-31" &&
                              !inv.return_date.startsWith("9999")
                              ? `🔄 ${new Date(
                                  inv.return_date
                                ).toLocaleDateString()}`
                              : "🔄 Return Date Missing"
                            : "🔄 Not Returned"}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {inv.referred_by ? (
                          <div className="text-sm text-gray-900">
                            <div>👤 {inv.referred_by}</div>
                            {inv.referrer_wallet && (
                              <div className="text-xs text-gray-500 font-mono">
                                🔑 {inv.referrer_wallet.substring(0, 10)}...
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">
                            No Referral
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        {inv.transaction_hash ||
                        inv.transaction_date ||
                        inv.transaction_remarks ? (
                          <div className="text-sm text-gray-900">
                            {inv.transaction_hash && (
                              <div className="text-xs text-gray-500 font-mono mb-1">
                                🔗 {inv.transaction_hash.substring(0, 10)}...
                                <button
                                  onClick={() =>
                                    copyToClipboard(inv.transaction_hash)
                                  }
                                  className="ml-1 text-blue-600 hover:text-blue-900"
                                >
                                  Copy
                                </button>
                              </div>
                            )}
                            {inv.transaction_date && (
                              <div className="text-xs text-gray-500">
                                📅 {inv.transaction_date}
                              </div>
                            )}
                            {inv.transaction_remarks && (
                              <div
                                className="text-xs text-gray-500"
                                title={inv.transaction_remarks}
                              >
                                💬{" "}
                                {inv.transaction_remarks.length > 20
                                  ? inv.transaction_remarks.substring(0, 20) +
                                    "..."
                                  : inv.transaction_remarks}
                              </div>
                            )}
                            {inv.from_wallet_address && (
                              <div className="text-xs text-gray-400 font-mono">
                                ⬅️ {inv.from_wallet_address.substring(0, 8)}...
                              </div>
                            )}
                            {inv.to_wallet_address && (
                              <div className="text-xs text-gray-400 font-mono">
                                ➡️ {inv.to_wallet_address.substring(0, 8)}...
                              </div>
                            )}
                          </div>
                        ) : (
                          <span className="text-gray-400 text-sm">
                            No Transaction Data
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Results Message */}
        {results.length === 0 && error === "" && (
          <div className="bg-white shadow rounded-lg">
            <div className="text-center py-12">
              <FunnelIcon className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No search results
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Try adjusting your search criteria or use the search button to
                find investors.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default InvestorsFilterPage;
